import type { ComponentProps, ReactNode } from "react"
import { ArrowLeft } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface InboxLayoutProps {
  header: ReactNode
  /** Kept on screen in every state — for a warning that changes what a reply will do. */
  notice?: ReactNode
  list: ReactNode
  pane: ReactNode
  hasSelection: boolean
  /** Defaults to mobile-only hiding while a conversation is selected. */
  hideSupportWidget?: "mobile" | "all" | false
}

/**
 * The inbox split view. From the `split` screen up the list and the pane sit
 * side by side; below it — narrow or short — one of them has the screen, and
 * `hasSelection` says which. CSS only, so it is right before hydration.
 *
 * The `data-app-scroll-pane` marker opts the shell out of phone document scrolling
 * (`app/(main)/globals.css`): this layout scrolls its list and its transcript inside
 * itself and keeps the composer on screen, which a scrolling document cannot do.
 * While a conversation is open below `split` the composer sits where the support
 * launcher does; the second marker hides the launcher for that state by default.
 * Pages can override this with `hideSupportWidget` independently of selection.
 */
export function InboxLayout({
  header, notice, list, pane, hasSelection,
  hideSupportWidget = hasSelection ? "mobile" : false,
}: InboxLayoutProps) {
  return (
    <div className="mx-auto flex h-full min-h-0 max-w-7xl flex-col p-3 split:p-6">
      <div className={cn("mb-3 flex-shrink-0", hasSelection && "hidden split:block")}>{header}</div>
      {notice && <div className="mb-2 flex-shrink-0">{notice}</div>}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border bg-background">
        <div
          data-testid="inbox-list"
          className={cn(
            // Never wider than the pane beside it: 40% of the body, floored so rows
            // stay readable, capped where a wide screen would waste it on the list.
            "flex w-full min-w-0 flex-col split:w-[clamp(280px,40%,440px)] split:flex-shrink-0 split:border-r",
            hasSelection && "hidden split:flex",
          )}
        >
          {list}
        </div>
        <div
          data-testid="inbox-pane"
          className={cn("flex min-w-0 flex-1 flex-col", !hasSelection && "hidden split:flex")}
        >
          {pane}
        </div>
      </div>

      <span data-app-scroll-pane hidden />
      {hideSupportWidget && <span data-hide-support-widget={hideSupportWidget} hidden />}
    </div>
  )
}

/** A header control that keeps its label from `split` up and its icon alone below — the label stays for screen readers. */
export function InboxHeaderButton({
  icon,
  label,
  className,
  ...props
}: ComponentProps<typeof Button> & { icon: ReactNode; label: string }) {
  return (
    <Button className={cn("px-2.5 split:px-4", className)} {...props}>
      {icon}
      <span className="sr-only split:not-sr-only">{label}</span>
    </Button>
  )
}

/** The pane's way back to the list below `split`, where opening a thread replaced it. */
export function InboxBackButton({ onBack }: { onBack: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="-ml-2 h-8 w-8 flex-shrink-0 split:hidden"
      aria-label="Back to conversations"
      title="Back to conversations"
      onClick={onBack}
    >
      <ArrowLeft className="h-4 w-4" />
    </Button>
  )
}
